import { ArrowLeft, ArrowRight } from 'lucide-react';
import { LiveChoiceButtons, type LiveChoiceOption } from './LiveChoiceButtons';
import type { ScoringPhase } from '../../../../../pages/magic-prioritization/types';
import {
  URGENCY_LABELS,
  VALUE_LABELS,
  DURATION_LABELS,
} from '../../../../../pages/magic-prioritization/types';

const PHASE_OPTIONS: Record<ScoringPhase, LiveChoiceOption[]> = {
  urgency: (Object.keys(URGENCY_LABELS) as Array<keyof typeof URGENCY_LABELS>).map(k => ({
    id: k,
    label: URGENCY_LABELS[k].label,
    hint: URGENCY_LABELS[k].description,
  })),
  value: (Object.keys(VALUE_LABELS) as Array<keyof typeof VALUE_LABELS>).map(k => ({
    id: k,
    label: VALUE_LABELS[k].label,
    hint: VALUE_LABELS[k].description,
  })),
  duration: (Object.keys(DURATION_LABELS) as Array<keyof typeof DURATION_LABELS>).map(k => ({
    id: k,
    label: DURATION_LABELS[k].label,
    hint: DURATION_LABELS[k].description,
  })),
};

const PHASE_PROMPTS: Record<ScoringPhase, string> = {
  urgency: 'How time-sensitive is this item?',
  value: 'How much impact would this create?',
  duration: 'How long until meaningful payoff?',
};

interface MagicPrioritizationLiveProps {
  itemName: string;
  phase: ScoringPhase;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNext: () => void;
  onBack?: () => void;
  canProceed: boolean;
  itemIndex: number;
  totalItems: number;
  phaseIndex: number;
}

export function MagicPrioritizationLive({
  itemName,
  phase,
  selectedId,
  onSelect,
  onNext,
  onBack,
  canProceed,
  itemIndex,
  totalItems,
  phaseIndex,
}: MagicPrioritizationLiveProps) {
  const totalSteps = totalItems * 3;
  const step = phaseIndex * totalItems + itemIndex;

  return (
    <div className="max-w-lg">
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 mb-4">
        <div className="font-heading text-[0.5rem] tracking-[0.1em] uppercase text-[var(--text-dim)] mb-1">
          {phase} · item {itemIndex + 1} of {totalItems}
        </div>
        <div className="font-body text-[0.85rem] font-medium text-[var(--text-pri)]">{itemName}</div>
      </div>

      <div className="font-heading text-[0.52rem] tracking-[0.14em] uppercase text-electric-mustard-600 mb-3 text-center">
        Dimension: {phase}
      </div>

      <LiveChoiceButtons
        prompt={PHASE_PROMPTS[phase]}
        options={PHASE_OPTIONS[phase]}
        selectedId={selectedId}
        onSelect={onSelect}
      />

      <div className="flex items-center justify-between mt-6 gap-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        ) : (
          <span />
        )}
        <span className="font-heading text-[0.5rem] text-[var(--text-dim)]">
          {totalSteps > 0 ? Math.round((step / totalSteps) * 100) : 0}% session
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="inline-flex items-center gap-1 h-8 px-3 font-heading text-[0.55rem] tracking-[0.1em] uppercase text-alpine-green-400 border border-alpine-green-500/45 bg-alpine-green-500/15 hover:bg-alpine-green-500/25 disabled:opacity-40"
        >
          Next
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
